<script setup lang="ts">
import VitaIcon from "@/components/UI/VitaIcon.vue";
import VitaButton from '@/components/UI/VitaButton.vue';
import { checkUnsavedChanges } from "@/utils/main";
import { vacationTypes } from "@/utils/vacation";
import store from "@/store";
import { computed, ref } from 'vue';
import { VacationItem } from '@/types';

defineProps<Props>();

interface Props {
  vacation: VacationItem;
  zupGuid: string;
  visible: boolean;
  visibleUpdate: string | null;
  userCanEdit: boolean;
}

const inProcess = ref(false);
const openConfirmModal = ref(false);
const emit = defineEmits(["update:visible", "update:visibleUpdate"]);

const openVacationUpdate = (vacationId: string | null | undefined) => {
  if (!vacationId) return
  emit("update:visible", true);
  emit("update:visibleUpdate", vacationId);
};

const loader = computed<boolean>(() => {
  return store.getters["user/loader"];
});

const cancelVacation = async (id: string | null | undefined, zupGuid: string) => {
  if (!id) return

  try {
    openConfirmModal.value = false;
    inProcess.value = true;
    !loader.value ? store.commit("user/setLoader", true) : null;
    const statusCode = await store.dispatch("data/cancelVacation", {
      id,
      zupGuid,
    });
    if (statusCode >= 400) return false;

    // noinspection ES6MissingAwait
    store.dispatch("data/getFondWorkTime");
    // noinspection ES6MissingAwait
    store.dispatch("data/getFondWorkTimeHuman");
    await store.dispatch("data/getPharmSchedule");
    await store.dispatch("data/getAdminShift");
    // Обновление расписания для незаполненных дней
    await store.dispatch("data/getTimeScheduleDetails");
    await store.dispatch("data/getTimeSchedule");

    // Закрытие окна
    store.commit("data/closeCreateShift");
    checkUnsavedChanges();
  } catch (e) {
    console.error(e);
  } finally {
    inProcess.value = false;
    store.commit("user/setLoader", false);
  }
};
</script>

<template>
  <div class="shift__vacation-item item-layout" v-if="vacation">
    <div class="item-layout-title" v-if="vacation.type">
      {{ vacationTypes[vacation.type].title }}
    </div>

    <div class="item-layout-controls" v-if="userCanEdit && vacation.isInternal && vacation.id">
      <button
        @click="openVacationUpdate(vacation.id)"
        type="button"
        data-qa="vacation-item-button-edit"
      >
        <vita-icon name="pencil" />
      </button>
      <button
        @click="openConfirmModal = true"
        type="button"
        data-qa="vacation-item-button-cancel"
      >
        <vita-icon name="cross-red" />
      </button>
    </div>
  </div>

  <vita-modal v-if="openConfirmModal" title="Вы действительно хотите отменить НН?">
    <vita-button
      color="red"
      width="inline"
      @click="cancelVacation(vacation.id, zupGuid)"
      :disabled="inProcess"
      data-qa="vacation-item-button-cancel-confirm"
    >Да</vita-button>
    <vita-button
      color="blue"
      width="inline"
      @click="openConfirmModal = false"
      :disabled="inProcess"
      data-qa="vacation-item-button-cancel-reject"
    >Нет</vita-button>
  </vita-modal>
</template>

<style scoped lang="scss">
.item-layout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  & + & {
    padding-top: 12px;
    border-top: 1px solid rgba(204, 204, 204, 1);
  }

  &-title {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
  }

  &-controls {
    display: flex;
    align-items: center;
    gap: 9px;
  }

  button {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    appearance: none;
    -webkit-appearance: none;
    outline: none;
    border: none;
    background: transparent;
    transition: opacity .15s ease;

    &:hover {
      opacity: .7;
    }

    &:active {
      opacity: 1;
    }
  }
}
</style>
